import React from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import { AggregationType, PositionSelector } from '../types/reportBuilder.types';
import { Question } from '../../../../api/types/forms';

interface AggregationBuilderProps {
  aggregation?: AggregationType;
  positionSelector?: PositionSelector;
  separator?: string;
  onAggregationChange: (aggregation?: AggregationType) => void;
  onPositionSelectorChange: (selector?: PositionSelector) => void;
  onSeparatorChange: (separator?: string) => void;
  questionType?: string;
  question?: Question;
}

const AggregationBuilder: React.FC<AggregationBuilderProps> = ({
  aggregation,
  positionSelector,
  separator,
  onAggregationChange,
  onPositionSelectorChange,
  onSeparatorChange,
  questionType,
  question,
}) => {
  // Show aggregation for:
  // 1. Multiselect/checkbox types (array values)
  // 2. Questions with FOR_EACH display logic (multiple answers per parent selection)
  const isArrayType = questionType === 'multiselect' || questionType === 'checkbox';
  const hasForEachLogic = question?.display_logic?.condition_type === 'for_each';
  const shouldShowAggregation = isArrayType || hasForEachLogic;

  if (!shouldShowAggregation) {
    return null;
  }

  const aggregations: { value: AggregationType; label: string }[] = [
    { value: 'concat', label: 'Concatenar todos' },
    { value: 'max', label: 'Máximo' },
    { value: 'min', label: 'Mínimo' },
    { value: 'avg', label: 'Promedio' },
    { value: 'count', label: 'Contar elementos' },
    { value: 'first', label: 'Primero' },
    { value: 'last', label: 'Último' },
    { value: 'nth', label: 'N-ésimo' },
    { value: 'all', label: 'Todos' },
  ];

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Agregación y Posición
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Agregación</InputLabel>
          <Select
            value={aggregation || ''}
            onChange={(e) => onAggregationChange(e.target.value as AggregationType || undefined)}
            label="Agregación"
          >
            <MenuItem value="">Sin agregación</MenuItem>
            {aggregations.map((agg) => (
              <MenuItem key={agg.value} value={agg.value}>
                {agg.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {aggregation === 'nth' && (
          <TextField
            size="small"
            label="Índice (0-based)"
            type="number"
            value={positionSelector?.index || 0}
            onChange={(e) =>
              onPositionSelectorChange({
                type: 'nth',
                index: parseInt(e.target.value, 10) || 0,
              })
            }
            inputProps={{ min: 0 }}
            sx={{ width: 150 }}
          />
        )}

        {!aggregation && (
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Posición</InputLabel>
            <Select
              value={positionSelector?.type || 'first'}
              onChange={(e) =>
                onPositionSelectorChange({
                  type: e.target.value as 'first' | 'last' | 'nth' | 'all',
                  index: e.target.value === 'nth' ? 0 : undefined,
                })
              }
              label="Posición"
            >
              <MenuItem value="first">Primero</MenuItem>
              <MenuItem value="last">Último</MenuItem>
              <MenuItem value="nth">N-ésimo</MenuItem>
              <MenuItem value="all">Todos</MenuItem>
            </Select>
          </FormControl>
        )}

        {aggregation === 'concat' && (
          <TextField
            size="small"
            label="Separador"
            value={separator || ', '}
            onChange={(e) => onSeparatorChange(e.target.value)}
            sx={{ width: 150 }}
          />
        )}
      </Box>
    </Box>
  );
};

export default AggregationBuilder;
